'use client';
import React, { useEffect, useState } from 'react';
import { DelayedQuote } from '@benzinga/quotes-manager';
import styled from '@benzinga/themetron';
import { Search } from '@benzinga/search-ui';
import { DefaultQuotesList } from './_mockMenu';
import { QuotesList } from './QuotesList';
import { StockSymbol } from '@benzinga/session';

interface QuoteBarProps {
  marketTickers?: string[];
  onInput?: () => string;
  quotes?: Record<StockSymbol, DelayedQuote> | null;
  recentTickers?: string[];
}

export const QuoteBar: React.FC<QuoteBarProps> = React.memo(({ marketTickers, quotes, recentTickers }) => {
  const [activeQuotes, setActiveQuotes] = useState('markets');
  const [tickers, setTickers] = useState<string[] | undefined>(marketTickers ?? DefaultQuotesList);
  const [tickerQuotes, setTickerQuotes] = useState<Record<StockSymbol, DelayedQuote> | null | undefined>(quotes);

  useEffect(() => {
    if (activeQuotes === 'recent') {
      setTickers(recentTickers ?? []);
      setTickerQuotes(undefined);
    } else if (activeQuotes === 'markets') {
      setTickers(marketTickers ?? DefaultQuotesList);
      setTickerQuotes(quotes);
    }
  }, [activeQuotes, marketTickers, quotes, recentTickers, setTickers]);

  const QuotesSwitcher = () => {
    return (
      <QuoteSwitcherWrapper className="quote-switcher-wrapper">
        <div className="quote-switcher-container">
          <SwitcherLinkBtn
            $active={activeQuotes === 'recent'}
            className="quote-switcher-button"
            data-action="Quote Bar Recent Click"
            onClick={() => setActiveQuotes('recent')}
          >
            Recent
          </SwitcherLinkBtn>
          <SwitcherLinkBtn
            $active={activeQuotes === 'markets'}
            className="quote-switcher-button"
            data-action="Quote Bar Markets Click"
            onClick={() => setActiveQuotes('markets')}
          >
            Markets
          </SwitcherLinkBtn>
          {/* <SwitcherLinkBtn active={activeQuotes === 'crypto'} className="quote-switcher-button" onClick={() => setActiveQuotes('crypto')}>
            Crypto
          </SwitcherLinkBtn>
          <SwitcherLinkBtn className="quote-switcher-button" active={activeQuotes === 'cannabis'} onClick={() => setActiveQuotes('cannabis')}>
            Cannabis
          </SwitcherLinkBtn> */}
        </div>
      </QuoteSwitcherWrapper>
    );
  };

  return (
    <QuoteBarContainer className="quote-bar-container">
      <div className="quote-bar-layout-width">
        <div className="quote-bar-wrapper">
          <QuotesSwitcher />
          <div className="quote-list-container">
            <QuotesList quotes={tickerQuotes} tickers={tickers} />
          </div>
          <div className="search-wrapper">
            <Search placeholder="Search Tickers, Companies or News..." />
          </div>
        </div>
      </div>
    </QuoteBarContainer>
  );
});
interface SwitchActiveProps {
  $active: boolean;
}

const QuoteBarContainer = styled.div`
  &.quote-bar-container {
    background: linear-gradient(180deg, #082132 0%, #1d446a 100%);
    /* background-color: ${({ theme }) => theme.colorPalette.gray50}; */
    /* border-bottom: solid 1px ${({ theme }) => theme.colorPalette.gray100};
    border-top: solid 1px ${({ theme }) => theme.colorPalette.gray100}; */

    .quote-bar-layout-width {
      max-width: 1400px;
      margin: 0 auto;
      height: 52px;
    }
    .quote-bar-wrapper,
    .quotes-list {
      display: flex;
      flex-grow: 1;
    }
    .quote-list-container {
      flex-grow: 1;
      overflow-x: auto;
      scrollbar-width: none;
      ::-webkit-scrollbar {
        display: none;
      }
    }
    .search-wrapper {
      width: 100%;
      max-width: 288px;
      .searchbar-wrapper {
        padding: 8px 12px;
        background-color: transparent;
        font-weight: bold;
        font-size: 16px;
        line-height: 15px;
        .search-icon {
          width: 32px;
        }
        input {
          font-size: 12px;
        }
        .searchbar-container {
          background-color: white;
          border-radius: 2px;
        }
        @media (min-width: 1300px) {
          padding-right: 0;
          .search-icon {
            width: 36px;
          }
        }
      }
      .search-icon {
        height: 36px;
      }
    }
  }
`;

const QuoteSwitcherWrapper = styled.div`
  &.quote-switcher-wrapper {
    overflow-y: auto;
    height: 52px;
    width: 70px;
    scrollbar-width: none;
    flex: none;
    ::-webkit-scrollbar {
      display: none;
    }
    .quote-switcher-container {
      display: flex;
      flex-direction: column;
    }
  }
`;

const SwitcherLinkBtn = styled.button<SwitchActiveProps>`
  &.quote-switcher-button {
    max-width: 73px;
    width: 100%;
    border-left: 1px solid
      ${({ $active, theme }) => ($active ? theme.colorPalette.gray100 : theme.colorPalette.gray400)};
    background: ${({ $active, theme }) =>
      $active
        ? `linear-gradient(90deg, ${theme.colorPalette.blue600}1a 0%, ${theme.colorPalette.blue600}00 100%)`
        : `linear-gradient(90deg, ${theme.colorPalette.gray400}1a 0%, ${theme.colorPalette.gray400}00 100%)`};
    text-align: left;
    color: ${({ $active, theme }) => ($active ? theme.colorPalette.white : theme.colorPalette.gray400)};
    font-size: ${({ theme }) => theme.fontSize.sm};
    line-height: 18px;
    padding: 4px 9px;
    flex-grow: 1;
    :focus {
      outline: none;
    }
  }
`;
